import {
   type CountryCode,
   type LanguageCode,
   ShopifyStorefrontProvider,
} from '@ifixit/shopify-storefront-client';
import { useAddToCart } from '@ifixit/shopify-cart-sdk/hooks';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import initializeComponent from 'Shared/react-initialize';
import { fireGlobal } from 'Shared/utils/global-events';
import { useEffect, useMemo } from 'react';
import type { GuideProduct, GuideProductsData } from './Product';
import type { GuideData } from './GuideHeader';
import { GuideStatsInteractions } from './GuideStatsInteractions';
import { withGuideSubscription } from './GuideSubscription';
import { withGuideFavorite } from './GuideFavorite';
import { withGuideCompletion } from './GuideComplete';

interface GuideTopProps {
   productData: GuideProductsData;
   guideData: GuideData;
   shopifyStore: {
      country: CountryCode;
      currencyCode: string;
      language: LanguageCode;
      storeCode: string;
      shopDomain: string;
      storefrontAccessToken: string;
   };
}

function GuideTopInteractions(props: GuideTopProps) {
   const root = useMemo(() => document.querySelector<HTMLElement>('[data-guide-top]'), []);
   useGuideNavigation(root);

   if (!root) return null;

   return (
      <ShopifyStorefrontProvider {...props.shopifyStore}>
         {!props.guideData.isTeardown && <GuideStatsInteractions root={root} />}
         {(['kits', 'parts', 'tools'] as const).flatMap(group =>
            props.productData[group].map((product, index) => {
               // Matches data-guide-product in top_product.phtml; the server list never reorders.
               const productKey = `${group}-${index}`;
               return (
                  <GuideProductInteraction
                     key={productKey}
                     root={root}
                     product={product}
                     productKey={productKey}
                     group={group}
                  />
               );
            })
         )}
      </ShopifyStorefrontProvider>
   );
}

export function GuideProductInteraction({
   root,
   product,
   productKey,
   group,
}: {
   root: HTMLElement;
   product: GuideProduct;
   productKey: string;
   group: string;
}) {
   const { addToCart, enabled } = useAddToCart();
   const { mutate, isPending } = addToCart;
   useEffect(() => {
      const item = root.querySelector<HTMLElement>(`[data-guide-product="${productKey}"]`);
      if (!item) return;
      const select = item.querySelector<HTMLSelectElement>('[data-guide-variant]');
      const button = item.querySelector<HTMLButtonElement>('[data-guide-add-to-cart]');
      const view = item.querySelector<HTMLAnchorElement>('[data-guide-view-product]');
      const selectedVariant = () =>
         select ? (product.variants[Number(select.value)] ?? null) : product.initialVariant;
      const updateLinks = () => {
         const variant = selectedVariant();
         const url = variant?.cartLine?.url ?? product.productUrl;
         item.querySelectorAll<HTMLAnchorElement>('[data-guide-product-link]').forEach(link => {
            link.href = url;
         });
         if (button) {
            button.hidden = !variant?.isInStock;
            button.disabled = !enabled || !variant?.isInStock || isPending;
         }
         if (view) view.hidden = Boolean(variant?.isInStock);
      };
      const add = () => {
         const variant = selectedVariant();
         if (!enabled || !variant?.isInStock || isPending) return;
         mutate({
            analytics: { descriptor: 'Guide Products', localeCode: product.localeCode },
            lines: [variant.cartLine],
         });
         fireGlobal('cart.addcomplete');
      };
      const track = () => {
         const url = selectedVariant()?.cartLine?.url ?? product.productUrl;
         const partOrTool = group === 'kits' ? 'Kit' : group === 'parts' ? 'Part' : 'Tool';
         trackAnalyticsCustomEvent({
            eventCategory: 'Guide Products',
            eventAction: `Guide Products - Click Product - ${partOrTool}${product.isExternal ? ' - External' : ''}`,
            eventName: product.isExternal
               ? `external:${product.name}:${url}`
               : `ifixit:${product.name}:${product.sku}`,
         });
      };
      updateLinks();
      select?.addEventListener('change', updateLinks);
      button?.addEventListener('click', add);
      item.addEventListener('click', track);
      return () => {
         select?.removeEventListener('change', updateLinks);
         button?.removeEventListener('click', add);
         item.removeEventListener('click', track);
         if (button) button.disabled = true;
      };
   }, [root, product, productKey, group, enabled, isPending, mutate]);
   return null;
}

function useGuideNavigation(root: HTMLElement | null) {
   useEffect(() => {
      if (!root) return;
      const image = root.querySelector<HTMLAnchorElement>('[data-guide-image]');
      const dialog = root.querySelector<HTMLDialogElement>('[data-guide-image-dialog]');
      const showImage = (event: MouseEvent) => {
         if (event.ctrlKey || event.metaKey || event.shiftKey || !dialog) return;
         event.preventDefault();
         const fullImage = dialog.querySelector<HTMLImageElement>('img[data-src]');
         if (fullImage && !fullImage.getAttribute('src'))
            fullImage.src = fullImage.dataset.src ?? '';
         dialog.showModal();
      };
      image?.addEventListener('click', showImage);

      const firstStep = root.querySelector<HTMLAnchorElement>('[data-guide-first-step]');
      const trackFirstStep = () =>
         trackAnalyticsCustomEvent({
            eventCategory: 'Guide Products',
            eventAction: 'Guide Products - Go to step 1',
         });
      firstStep?.addEventListener('click', trackFirstStep);

      const expanded = root.querySelector<HTMLInputElement>('[data-guide-products-expanded]');
      const products = root.querySelector<HTMLElement>('[data-guide-products]');
      // The server only pre-expands lists it's sure fit, so catch the rest here.
      const fits = products && products.scrollHeight <= products.clientHeight;
      if (expanded && (fits || window.history.state?.productsExpanded)) expanded.checked = true;
      const rememberExpanded = () => {
         window.history.replaceState(
            { ...window.history.state, productsExpanded: expanded?.checked },
            ''
         );
         trackAnalyticsCustomEvent({
            eventCategory: 'Guide Products',
            eventAction: 'Guide Products - Show more',
         });
      };
      expanded?.addEventListener('change', rememberExpanded);

      const flags = [...root.querySelectorAll<HTMLDetailsElement>('[data-guide-flag]')];
      const closeFlags = (event: PointerEvent) => {
         flags.forEach(flag => {
            if (event.target instanceof Node && !flag.contains(event.target)) flag.open = false;
         });
      };
      document.addEventListener('pointerdown', closeFlags);
      const escapeFlags = (event: KeyboardEvent) => {
         if (event.key !== 'Escape') return;
         flags.forEach(flag => {
            if (!flag.open) return;
            const hadFocus = flag.contains(document.activeElement);
            flag.open = false;
            if (hadFocus) flag.querySelector('summary')?.focus();
         });
      };
      document.addEventListener('keydown', escapeFlags);
      return () => {
         image?.removeEventListener('click', showImage);
         firstStep?.removeEventListener('click', trackFirstStep);
         expanded?.removeEventListener('change', rememberExpanded);
         document.removeEventListener('pointerdown', closeFlags);
         document.removeEventListener('keydown', escapeFlags);
      };
   }, [root]);
}

initializeComponent(
   'GuideTopInteractions',
   withGuideCompletion(withGuideSubscription(withGuideFavorite(GuideTopInteractions)))
);
